import * as React from 'react';
import Profile from './Profile';

// TypeScript declarations for global DDSJobs utilities
declare global {
  interface Window {
    DDSJobs?: {
      isBot: () => boolean;
    };
    Sentry?: {
      captureException: (error: Error, context?: any) => void;
    };
  }
}

export const ProfileListContext = React.createContext<{ blockList?: boolean }>({});

interface ProfileData {
  id: number;
  [key: string]: any;
}

interface Job {
  id: number;
  position_type: string;
  closed?: boolean;
  temp_filled?: boolean;
}

interface Result {
  _id: number;
  fields: {
    distance?: string;
  };
}

interface InviteCandidatesParams {
  job: Job;
  is_temp: boolean;
  authenticity_token: string;
  callback: (results: number[]) => void;
  ids: number[];
}

interface ProfileSearchProfileListProps {
  profiles: ProfileData[];
  results?: Result[];
  authenticity_token: string;
  favorites?: any[];
  job?: Job;
  requested?: number[];
  accepted?: number[];
  declined?: number[];
  user_id?: number;
  blockList?: boolean;
}

const inviteCandidates = ({ job, is_temp, authenticity_token, callback, ids }: InviteCandidatesParams) => {
  const url = is_temp
    ? `/jobs/${job.id}/request.json`
    : `/jobs/${job.id}/invite.json`;

  // Check if this is a bot to prevent unnecessary API calls
  if (window.DDSJobs && window.DDSJobs.isBot()) {
    console.warn('Bot detected, skipping candidate invitation API call');
    return;
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': authenticity_token,
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ ids }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data && data.results) {
        callback(data.results);
      } else {
        console.warn('Invalid response format from candidate invitation API:', data);
        callback([]);
      }
    })
    .catch(error => {
      console.error('Error inviting candidates:', error);
      
      // Report to Sentry if available
      if (typeof window.Sentry !== 'undefined') {
        window.Sentry.captureException(error, {
          tags: {
            component: 'candidate_invitation',
            job_id: job.id,
            is_temp: is_temp
          },
          extra: {
            url,
            candidate_ids: ids,
            candidates_count: ids.length
          }
        });
      }
      
      // Call callback with empty results to prevent UI from hanging
      callback([]);
    });
};

// Unblock candidate API function
const unblockCandidate = async ({ profile_id, authenticity_token, user_id }: {
  profile_id: number;
  authenticity_token: string;
  user_id?: number;
}): Promise<boolean> => {
  const url = `/profiles/${profile_id}/remove_block_candidate_user/${user_id}`;
  
  // Check if this is a bot to prevent unnecessary API calls
  if (window.DDSJobs && window.DDSJobs.isBot()) {
    console.warn('Bot detected, skipping unblock candidate API call');
    return false;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': authenticity_token,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const res = await response.json();
    
    if (res && res.status && typeof res.status.favorited === 'boolean') {
      return res.status.favorited;
    } else {
      console.warn('Invalid response format from unblock candidate API:', res);
      return false;
    }
  } catch (error) {
    console.error('Error unblocking candidate:', error);
    
    // Report to Sentry if available
    if (typeof window.Sentry !== 'undefined') {
      window.Sentry.captureException(error, {
        tags: {
          component: 'candidate_unblock',
          profile_id: profile_id,
          user_id: user_id
        },
        extra: {
          url
        }
      });
    }
    
    // Return false to indicate operation failed
    return false;
  }
};

const arrayEquals = (a: number[], b: number[]): boolean => {
  return Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index]);
};

export const ListContext = React.createContext<[
  { blocked: number[] },
  React.Dispatch<React.SetStateAction<{ blocked: number[] }>>
]>([
  { blocked: [] },
  () => { }
]);

/**
 * ProfileSearchProfileList Component
 * 
 * Matches the original master branch functionality for profile listing.
 * Handles both regular profile display and blocked candidate management.
 * Converted from JS to TypeScript while maintaining original functionality.
 * 
 * Features:
 * - Profile listing with individual actions
 * - Bulk invitation/request capabilities
 * - Block list support for blocked candidates
 * - Loading states and progress indicators
 * - Distance-based sorting and display
 * 
 * Use Cases:
 * - Blocked candidates listing (/profiles/blocked)
 * - Profile search results display
 * - Recruitment workflow management
 */
const ProfileSearchProfileList: React.FC<ProfileSearchProfileListProps> = ({
  profiles,
  results = [],
  authenticity_token,
  favorites = [],
  job,
  requested = [],
  accepted = [],
  declined = [],
  user_id,
  blockList = false
}) => {
  const [state, setState] = React.useState({
    requested,
    accepted,
    declined,
    loading: false
  });

  const [context, setContext] = React.useState({ blocked: [] });

  const addToRequested = (id: number) =>
    setState({ ...state, requested: [...state.requested, id] });

  const addAllToRequested = (ids: number[]) =>
    setState({ ...state, requested: [...state.requested, ...ids], loading: false });

  const handleUnblockCandidate = async (profileId: number) => {
    try {
      setState({ ...state, loading: true });
      const success = await unblockCandidate({
        profile_id: profileId,
        authenticity_token,
        user_id
      });
      
      if (success) {
        // Remove from blocked context and refresh page or remove from list
        setContext({
          ...context,
          blocked: context.blocked.filter(id => id !== profileId)
        });
        setState({ ...state, loading: false });
        // Optionally refresh the page to remove the profile from the list
        window.location.reload();
      } else {
        // Handle failure case
        setState({ ...state, loading: false });
        console.warn('Failed to unblock candidate - API returned false');
        
        // Could show user-facing error message here
        // For now, just log the failure
      }
    } catch (error) {
      console.error('Error unblocking candidate:', error);
      setState({ ...state, loading: false });
      
      // The error is already handled and reported in the unblockCandidate function
      // Just ensure loading state is reset
    }
  };

  const is_temp = job && job.position_type === 'temp';

  const ids = profiles.map(profile => profile.id);

  const requested_all = arrayEquals(state.requested.sort(), ids.sort());

  return (
    <div className="container mt-4">
      <div className="padding-right" key={requested.join(',')}>
        <ListContext.Provider value={[context, setContext]}>
          {state.loading && (
            <div style={{ width: '100%', textAlign: 'right', marginBottom: 10 }}>
              Loading...
            </div>
          )}
          {!state.loading && job && !job.closed && !job.temp_filled && requested_all && profiles.length > 0 && (
            <div style={{ width: '100%', textAlign: 'right', marginBottom: 10 }}>
              <a className="button white">
                <i className="fad fa-check"></i> {is_temp ? "Requested" : "Invited"} All
              </a>
            </div>
          )}
          {!state.loading && job && !job.closed && !job.temp_filled && !requested_all && (
            <div style={{ width: '100%', textAlign: 'right', marginBottom: 10 }}>
              <a
                className="button"
                onClick={() => {
                  setState({ ...state, loading: true });
                  inviteCandidates({
                    is_temp: !!is_temp,
                    authenticity_token,
                    callback: (e) => {
                      addAllToRequested(e);
                    },
                    ids: ids.filter(item => context.blocked.indexOf(item) < 0),
                    job: job!
                  });
                }}
              >
                <i className="fad fa-users"></i> {is_temp ? "Request" : "Invite"} All
              </a>
            </div>
          )}
          <ProfileListContext.Provider value={{ blockList }}>
            <div className="table-responsive">
              <table className="table table-hover">
                <tbody>
                  {profiles.map(profile => {
                    const result = results.find(r => r._id === profile.id);
                    const distance = result ? result.fields.distance : false;

                    return (
                      <tr key={profile.id} className={context.blocked.includes(profile.id) ? 'table-secondary' : ''}>
                        <td>
                          <div className="d-flex align-items-center">
                            <img
                              src={profile.image || '/images/missing.svg'}
                              alt={`${profile.first_name} ${profile.last_name}'s avatar`}
                              className="rounded-circle me-3"
                              style={{ width: 50, height: 50, objectFit: 'cover' }}
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <a
                                    href={profile.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-decoration-none fw-bold text-dark h5 mb-1"
                                  >
                                    {profile.first_name} {profile.last_name}
                                  </a>
                                  <div className="text-muted small mb-2">
                                    {profile.city && profile.state && (
                                      <>
                                        <i className="fas fa-map-marker-alt me-1"></i>
                                        {profile.city}, {profile.state}
                                        {distance && (
                                          <span className="ms-2">
                                            <i className="fas fa-route me-1"></i>
                                            {distance} miles
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    {profile.job_types && profile.job_types.length > 0 && (
                                      profile.job_types.slice(0, 3).map((jobType: any, index: number) => (
                                        <span key={index} className="badge bg-primary text-white">
                                          {typeof jobType === 'object' ? (jobType.title || jobType.name || 'Job Type') : jobType}
                                        </span>
                                      ))
                                    )}
                                    {profile.job_types && profile.job_types.length > 3 && (
                                      <span className="badge bg-secondary text-dark">
                                        +{profile.job_types.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                  {profile.skills && profile.skills.length > 0 && (
                                    <div className="d-flex flex-wrap gap-1">
                                      {profile.skills.slice(0, 5).map((skill: any, index: number) => (
                                        <span key={index} className="badge bg-light text-dark border">
                                          {typeof skill === 'object' ? (skill.title || skill.name || 'Skill') : skill}
                                        </span>
                                      ))}
                                      {profile.skills.length > 5 && (
                                        <span className="badge bg-light text-dark border">
                                          +{profile.skills.length - 5} more skills
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="text-end">
                                  {job && !job.closed && !job.temp_filled && (
                                    <div className="mb-2">
                                      {state.requested.includes(profile.id) ? (
                                        <span className="badge bg-success">
                                          <i className="fas fa-check me-1"></i>
                                          {is_temp ? "Requested" : "Invited"}
                                        </span>
                                      ) : state.accepted.includes(profile.id) ? (
                                        <span className="badge bg-info">
                                          <i className="fas fa-thumbs-up me-1"></i>
                                          Accepted
                                        </span>
                                      ) : state.declined.includes(profile.id) ? (
                                        <span className="badge bg-warning">
                                          <i className="fas fa-thumbs-down me-1"></i>
                                          Declined
                                        </span>
                                      ) : (
                                        <button
                                          className="btn btn-primary btn-sm"
                                          onClick={() => addToRequested(profile.id)}
                                          disabled={state.loading}
                                        >
                                          <i className="fas fa-user-plus me-1"></i>
                                          {is_temp ? "Request" : "Invite"}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  <div className="d-flex gap-2">
                                    {/* Unblock button - only show for blocked candidates */}
                                    {blockList && (
                                      <button
                                        className="btn btn-warning btn-sm"
                                        onClick={() => handleUnblockCandidate(profile.id)}
                                        disabled={state.loading}
                                        title="Remove from blocked list"
                                      >
                                        <i className="fas fa-unlock me-1"></i>
                                        Unblock
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ProfileListContext.Provider>
        </ListContext.Provider>
      </div>
    </div>
  );
};

export default ProfileSearchProfileList;
